import { SerializedError } from '@reduxjs/toolkit';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import isArray from 'lodash/fp/isArray';
import isUndefined from 'lodash/fp/isUndefined';
import { toast } from 'sonner';

import { getIsInReceiveSession } from '@utils/helpers';
import { getCompanyFromHost, isHostValid } from '@utils/url';

import { setErrorMFA } from '@redux/slices/auth';

import type { RootState } from '../store';

class FetchQueryError extends Error {
  constructor(error: FetchBaseQueryError, status: string | number = '') {
    super(error.data as string);
    this.name = `FetchQueryError: ${status}`;
  }
}

const ignoreMessages = ["doesn't exist. Error: Connection"];

export const getErrorMessage = (error?: FetchBaseQueryError | SerializedError) => {
  if (!error || 'message' in error) return null;
  if ('data' in error) {
    if ((error.data as any)?.userMessage) {
      return (error.data as any).userMessage as string;
    }
    if (error.status === 500) {
      return 'Server error';
    }
  }
  return null;
};

const generateQueryApi = (url: string) =>
  fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      if (isHostValid()) {
        headers.set('X-Company-Subdomain', getCompanyFromHost());
      }
      return headers;
    },
    paramsSerializer: (params) => {
      const parsedPArams = Object.keys(params).reduce((acc: Array<any>, key) => {
        if (isUndefined(params[key])) return acc;
        if (isArray(params[key])) {
          params[key].forEach((value: any) => {
            if (!isUndefined(value)) acc.push([key, value]);
          });
        } else {
          acc.push([key, params[key]]);
        }

        return acc;
      }, []);
      return new URLSearchParams(parsedPArams).toString();
    },
  });

const baseQuery = generateQueryApi(`${import.meta.env.VITE_APP_API}/api`);

const lockersQuery = generateQueryApi(`${import.meta.env.VITE_APP_API_LOCKERS}/api`);

const identityQuery = generateQueryApi(`${import.meta.env.VITE_APP_IDENTITY_API}/api`);

const baseQueryWithNotifications: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  try {
    const result = await baseQuery(args, api, extraOptions);
    const isInReceiveSession = getIsInReceiveSession();

    if (
      result.data &&
      (result.data as any).userMessage &&
      api.endpoint !== 'updatePieces' &&
      api.endpoint !== 'createPieceDocument'
    ) {
      toast.success((result.data as any).userMessage, { duration: 3000, className: isInReceiveSession ? 'dark' : '' });
    }

    if (result.error) {
      if (result.error.data === 'CompanyRequiresMfa') {
        api.dispatch(setErrorMFA(true));
      }
      const message = getErrorMessage(result.error);
      if (message && !ignoreMessages.some((im) => message.includes(im))) {
        toast.error(message, { duration: 3000, className: isInReceiveSession ? 'dark' : '' });
      }

      const status = result.meta?.response?.status || result.error.status;

      Sentry.captureException(new FetchQueryError(result.error, status), (scope) => {
        scope.setTransactionName(message || (result.error.data as any)?.userMessage);
        scope.setExtras({ error: result.error, request: args });
        return scope;
      });
    }

    return result;
  } catch (e) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: String(e),
        data: e,
      },
    };
  }
};

export const lockersApi = createApi({
  reducerPath: 'lockersApiData',
  baseQuery: lockersQuery,
  tagTypes: [],
  endpoints: () => ({}),
});

export const identityApi = createApi({
  reducerPath: 'identityApiData',
  baseQuery: identityQuery,
  tagTypes: [],
  endpoints: () => ({}),
});

export const receivedDigitalApi = createApi({
  reducerPath: 'receivedDigitalApiData',
  baseQuery: baseQueryWithNotifications,
  tagTypes: [
    'Cards',
    'Company',
    'CompanyVariable',
    'Container',
    'Containers',
    'Mailroom',
    'Piece',
    'Pieces',
    'PiecesCounters',
    'User',
    'Users',
    'UsersMin',
    'UsersInfinite',
    'Settings',
    'Subscription',
    'PieceAutoAction',
    'RSPiece',
    'RS',
    'PieceAnnotations',
    'DeliveryRoutes',
    'Location',
    'ConnectInstance',
    'shippingLabelApi',
    'NotificationTemplate',
    'UserGroups',
    'LocationUsers',
    'GroupUsers',
    'PieceAutoActionConflicts',
  ],
  endpoints: () => ({}),
});
